import { get } from 'lodash';
import turf from '@turf/turf';

import { calculateHomographyMatrix } from '../homography-matrix-calculation';

/**
 * Calculates the geographical coordinates corresponding to a point in the video.
 *
 * @param {object} arg - The arguments for the function.
 * @param {number[][]} arg.geoJSONCoordinates - The geographical coordinates of the 4 projected corners
 * of the video.
 * @param {number[]} arg.videoCoordinates - The video point coordinates in pixels.
 * @param {number} arg.videoTop - The video upper limit in pixels.
 * @param {number} arg.videoLeft - The video left limit in pixels.
 * @param {number} arg.videoWidth - The video width in pixels.
 * @param {number} arg.videoHeight - The video height in pixels.
 * @returns {number[]} The geographical coordinates of the projected video point.
 */
export const getTransformatedVideoCoordinates = ({
  geoJSONCoordinates,
  videoCoordinates,
  videoTop,
  videoLeft,
  videoWidth,
  videoHeight
}) => {
  const originPoints = {
    x1: videoLeft,
    y1: videoTop,
    x2: videoLeft,
    y2: videoTop + videoHeight,
    x3: videoLeft + videoWidth,
    y3: videoTop,
    x4: videoLeft + videoWidth,
    y4: videoTop + videoHeight
  };
  const destinationPoints = {
    x1: geoJSONCoordinates[1][0],
    y1: geoJSONCoordinates[1][1],
    x2: geoJSONCoordinates[2][0],
    y2: geoJSONCoordinates[2][1],
    x3: geoJSONCoordinates[0][0],
    y3: geoJSONCoordinates[0][1],
    x4: geoJSONCoordinates[3][0],
    y4: geoJSONCoordinates[3][1]
  };

  const transformationMatrix = calculateHomographyMatrix({ originPoints, destinationPoints });

  const lambda =
    transformationMatrix[6] * videoCoordinates[0] +
    transformationMatrix[7] * videoCoordinates[1] +
    transformationMatrix[8];
  const lng =
    (transformationMatrix[0] * videoCoordinates[0] +
      transformationMatrix[1] * videoCoordinates[1] +
      transformationMatrix[2]) /
    lambda;
  const lat =
    (transformationMatrix[3] * videoCoordinates[0] +
      transformationMatrix[4] * videoCoordinates[1] +
      transformationMatrix[5]) /
    lambda;

  return [lng, lat];
};

/**
 * Gets the geographical coordinates for the video clicked point.
 *
 * @param {object} arg - The arguments for the function.
 * @param {number[]} arg.videoCoordinates - The screen coordinates of the video clicked point.
 * @param {object} arg.feature - The current position in the video track.
 * @param {object} arg.boundingRect - The screen bounding box of the video element.
 * @returns {object} The geographical coordinates for the video clicked point.
 */
const getMapCoordinatesForPixelPoint = ({ videoCoordinates, feature, boundingRect }) => {
  const { top: videoTop, left: videoLeft, width: videoWidth, height: videoHeight } = boundingRect;

  const GPSCP = turf.getCoord(feature);
  const defaultCoordinateArray = [GPSCP, GPSCP, GPSCP, GPSCP];
  const geoJSONCoordinates = get(feature, 'properties.coordinates', defaultCoordinateArray);

  const newCoordinates = getTransformatedVideoCoordinates({
    geoJSONCoordinates,
    videoCoordinates,
    videoTop,
    videoLeft,
    videoWidth,
    videoHeight
  });

  return newCoordinates;
};

export default getMapCoordinatesForPixelPoint;
